import { IWidgetDefinition, IWidget } from '../../common/core';
import { IFusePaginationSettings, IFusePaginationState } from './fuse-pagination.settings';

const template = require('./fuse-pagination.html');
const styles = require('./fuse-pagination.scss').toString();

export class FusePaginationData {
  pageNumbers: number[] = [];
  lastRequest: string = null;

  constructor(public widget: IWidget<FusePaginationData, IFusePaginationSettings, IFusePaginationState>) {
    if (!widget.service.seriesParam.per_page) {
      widget.service.seriesParam.per_page = 20;
    }
  }

  get page() {
    if (this.widget.state.page !== undefined) {
      return this.widget.state.page;
    }

    return 1;
  }

  set page(page) {
    if (page != 1) {
      this.widget.state.page = page;
    } else {
      delete this.widget.state.page;
    }
  }

  getPageNumbers(total: number) {
    // prevent loading more than 9999 items
    const pageCount = Math.min(Math.floor(9999 / this.widget.service.seriesParam.per_page), Math.ceil(total / this.widget.service.seriesParam.per_page));
    this.pageNumbers = this.range(1, pageCount + 1);
  }

  onPageClick(pageNumber: number) {
    this.page = pageNumber;

    this.refreshPagging();
  }

  onPreviousClick() {
    if (this.page > 1) {
      this.page --;

      this.refreshPagging();
    }
  }

  onNextClick() {
    if (this.page < this.pageNumbers.length) {
      this.page ++;

      this.refreshPagging();
    }
  }

  setSeriesParamsPage() {
    this.widget.service.seriesParam.page = this.page;
  }

  afterInit() {
    return new Promise<void>((resolve, reject) => {
      this.setSeriesParamsPage();

      this.widget.awaitGlobalQuery().then(currentQuery => {
        this.lastRequest = currentQuery;

        resolve();
      }, () => {
        reject();
      });

      this.widget.service.on('stateGlobalSet', () => {
        this.widget.awaitGlobalQuery().then(currentQuery => {
          this.lastRequest = currentQuery;
        });
      });
    });
  }

  beforeStateSet() {
    return new Promise<void>(resolve => {
      this.widget.awaitGlobalQuery().then(currentQuery => {
        // if query was changed than reset page number
        if (this.page != 1 && this.lastRequest != currentQuery) {
          this.page = 1;
        }

        this.lastRequest = currentQuery;
        this.setSeriesParamsPage();

        resolve();
      });
    });
  }

  get beginPages() {
    return Math.min(this.widget.settings.beginPages || 5, this.pageNumbers?.length || 0);
  }

  get endPages() {
    return Math.min(this.widget.settings.endPages || 5, this.pageNumbers?.length || 0);
  }

  get sidePages() {
    return this.widget.settings.sidePages || 1;
  }

  get beginPageNumbers() {
    const pageNumbers = [1];

    if (this.page < this.beginPages) {
      pageNumbers.push(...this.range(2, this.beginPages + 1));
    }

    return pageNumbers;
  }

  get endPageNumbers() {
    const pageNumbers: number[] = [];
    const totalPages = this.pageNumbers?.length || 0;
    const endPageStart = Math.max(totalPages - this.endPages + 1, 1);

    if (this.page > endPageStart) {
      pageNumbers.push(...this.range(endPageStart, totalPages));
    }
    pageNumbers.push(totalPages);

    return this.difference(pageNumbers, this.beginPageNumbers);
  }

  get centerPageNumbers() {
    const totalPages = this.pageNumbers?.length || 0;
    const centerPageStart = Math.max(this.page - this.sidePages, 1);
    const centerPageEnd = Math.min(this.page + this.sidePages, totalPages);
    
    const pageNumbers = this.range(centerPageStart, centerPageEnd + 1);

    return this.difference(pageNumbers, this.beginPageNumbers.concat(this.endPageNumbers));
  }

  get showCenterEllipsis() {
    return this.centerPageNumbers.length === 0 &&
      (this.beginPageNumbers.length + this.endPageNumbers.length < this.pageNumbers.length);
  }

  get showBeginEllipsis() {
    return this.beginPageNumbers.length > 0 &&
      this.centerPageNumbers.length > 0 &&
      this.centerPageNumbers[0] - this.beginPageNumbers[this.beginPageNumbers.length-1] > 1;
  }

  get showEndEllipsis() {
    return this.centerPageNumbers.length > 0 &&
      this.endPageNumbers.length > 0 &&
      this.endPageNumbers[0] - this.centerPageNumbers[this.centerPageNumbers.length-1] > 1;
  }

  get isScrollToTop() {
    // scroll if settings.scrollTop is null/undefined/true
   return this.widget.settings.scrollTop !== false; 
  }

  refreshPagging() {
    this.setSeriesParamsPage();
    this.widget.service.setState();

    if (this.isScrollToTop) {
      this.scrollToTop();
    }
  }

  scrollToTop() {
    const elements = document.getElementsByTagName('sjsearch-app');
    const el = elements && elements[0];

    if (el) {
      el.scrollIntoView();
    }
  }

  range(a: number, b: number) {
    const ret: number[] = [];
    const len = b ? b : a;
    let i = b ? a : 0;
  
    for (; i < len; i ++) {
      ret.push(i);
    }

    return ret;
  }

  difference(a: number[], b: number[]) {
    return a.filter(value => !b.includes(value));
  }  
};

export const FusePaginationWidgetDefinition: IWidgetDefinition<FusePaginationData, IFusePaginationSettings, IFusePaginationState> = {
  init(widget) {
    widget.data = new FusePaginationData(widget);
  },
  afterInit(widget) {
    return widget.data.afterInit();
  },
  /*
  beforeSearch(widget, requestArgs) {
    // no necessary now, but maybe this will be necessary in some other place in feature
    widget.service.seriesParam.page = widget.data.page;

    // add page number to request (also work for back/forward)
    requestArgs.page = widget.data.page;
  },
  */
  beforeStateSet(widget) {
    return widget.data.beforeStateSet();
  },
  onData(widget, data) {
    widget.data.getPageNumbers(data.total);
  },
  template,
  styles,
};
